import { useState } from 'react';
import { Menu } from '@mui/material';
import { TimecodeInput } from 'weave-timeline';
import { color } from '@/colors';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { CaretIcon } from '@/UI/Icons';
import { FlexCenVer } from '@/UI/styles';
import { FormatMenuItem } from './FormatMenuItem';
import { FrameInput } from './FrameInput';

type Mode = 'timecode' | 'frames';
const DEFAULT_TIME_INPUT_FPS = 30;
const TIME_INPUT_WIDTH = 74;

interface EditableTimeInputProps {
  currentTime: number;
  duration: number;
  fps?: number;
  paused: boolean;
  onTogglePlay: () => void;
  onSetTime: (time: number, ongoing: boolean) => void;
}

export function EditableTimeInput({
  currentTime,
  duration,
  fps,
  paused,
  onTogglePlay,
  onSetTime,
}: EditableTimeInputProps) {
  const [mode, setMode] = useState<Mode>('timecode');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const showFrames = mode === 'frames' && fps !== undefined;
  const activeMode: Mode = showFrames ? 'frames' : 'timecode';

  return (
    <FlexCenVer sx={{ gap: 0.25, flexShrink: 0, width: 'fit-content' }}>
      {showFrames ? (
        <FrameInput
          currentTime={currentTime}
          duration={duration}
          fps={fps}
          paused={paused}
          onTogglePlay={onTogglePlay}
          onSetTime={onSetTime}
        />
      ) : (
        <TimecodeInput
          value={currentTime}
          duration={duration}
          frameRate={fps ?? DEFAULT_TIME_INPUT_FPS}
          onChange={(time) => onSetTime(time, false)}
          onEditingStart={() => {
            if (!paused) onTogglePlay();
          }}
          style={{ width: TIME_INPUT_WIDTH, flexShrink: 0 }}
        />
      )}
      <AppIconButton
        width={24}
        height={24}
        mode="on-dark"
        sx={{ color: color.White64_T }}
        onClick={(event) => setMenuAnchor(event.currentTarget)}
      >
        <CaretIcon />
      </AppIconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: 120,
              background: color.Black92,
              border: `1px solid ${color.White04_T}`,
              borderRadius: '8px',
              p: 0.5,
              boxShadow: 'none',
            },
          },
          list: { sx: { py: 0 } },
        }}
      >
        <FormatMenuItem
          label="Timecode"
          active={activeMode === 'timecode'}
          onSelect={() => {
            setMode('timecode');
            setMenuAnchor(null);
          }}
        />
        <FormatMenuItem
          label="Frames"
          active={activeMode === 'frames'}
          disabled={fps === undefined}
          onSelect={() => {
            setMode('frames');
            setMenuAnchor(null);
          }}
        />
      </Menu>
    </FlexCenVer>
  );
}
